import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieById, getRelatedMovies, movieCategories } from '@/mocks/movies';

export default function MoviePage() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;
  const relatedMovies = useMemo(() => (movie ? getRelatedMovies(movie) : []), [movie]);

  if (!movie) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <p className="text-foreground-400">电影不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  const category = movieCategories.find((c) => c.slug === movie.category);
  const categoryLabel = category?.name || movie.subCategory;

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${movie.title} - ${categoryLabel}在线观看 - 天美影视`,
    description: `${movie.title}(${movie.originalTitle})，${movie.year}年${movie.region}${categoryLabel}，主演${movie.cast.slice(0, 3).join('、')}。天美影视高清免费在线观看。${movie.description.substring(0, 80)}`,
    url: `https://www.tianmeiying.com/movie/${movie.id}`,
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${movie.title} - ${categoryLabel}高清在线观看`,
    description: movie.description,
    datePublished: movie.releaseDate,
    author: {
      '@type': 'Organization',
      name: '天美影视',
    },
    publisher: {
      '@type': 'Organization',
      name: '天美影视',
    },
    image: movie.posterSrc,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.tianmeiying.com/movie/${movie.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.tianmeiying.com/' },
      { '@type': 'ListItem', position: 2, name: categoryLabel, item: `https://www.tianmeiying.com/category/${movie.category}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  return (
    <>
      <SchemaOrg data={webPageSchema} />
      <SchemaOrg data={articleSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <Navbar />
      <ScrollToTop />
      <main>
        <article className="bg-background-200 min-h-screen">
          <div className="relative w-full h-[320px] md:h-[450px] overflow-hidden">
            <img
              src={movie.coverSrc}
              alt={`${movie.title} 电影剧照`}
              title={`${movie.title} ${movie.subCategory}电影高清在线观看`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-200 via-background-950/60 to-background-950/40"></div>
          </div>

          <div className="w-full px-4 md:px-6 pb-10 relative -mt-32 md:-mt-40 z-10">
            <div className="max-w-[1100px] mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryLabel, href: `/category/${movie.category}` },
                  { label: movie.title },
                ]}
              />

              <div className="flex flex-col md:flex-row gap-6 md:gap-8 animate-fade-in-up animation-fill-both">
                <div className="flex-shrink-0 w-[180px] md:w-[240px] mx-auto md:mx-0">
                  <div className="aspect-[2/3] rounded-xl overflow-hidden border border-background-600/40 shadow-lg">
                    <img
                      src={movie.posterSrc}
                      alt={`${movie.title} 电影海报`}
                      title={`${movie.title} ${movie.subCategory}电影海报`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 pt-2">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">
                    {movie.title}
                  </h1>
                  <p className="text-sm text-foreground-500 mb-4">{movie.originalTitle}</p>

                  <div className="flex items-center gap-3 mb-5 flex-wrap">
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-500/10 text-primary-400 text-xs font-medium">
                      <i className="ri-star-fill"></i>
                      {movie.rating} 分
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-background-700/60 text-foreground-400 text-xs">
                      {movie.year}
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-background-700/60 text-foreground-400 text-xs">
                      {movie.duration}
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-background-700/60 text-foreground-400 text-xs">
                      {movie.region}
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-background-700/60 text-foreground-400 text-xs">
                      {movie.language}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-5 flex-wrap">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 rounded bg-accent-500/10 text-accent-400 text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-foreground-500 whitespace-nowrap min-w-[60px]">导演：</span>
                      <span className="text-foreground-300">{movie.director}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-foreground-500 whitespace-nowrap min-w-[60px]">主演：</span>
                      <span className="text-foreground-300">{movie.cast.join(' / ')}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-foreground-500 whitespace-nowrap min-w-[60px]">上映：</span>
                      <span className="text-foreground-300">{movie.releaseDate}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <button className="px-5 py-2.5 bg-primary-500 text-background-950 font-semibold rounded-full text-sm hover:bg-primary-400 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2">
                      <i className="ri-play-circle-fill text-lg"></i>
                      立即播放
                    </button>
                    <button className="px-5 py-2.5 bg-background-700/60 text-foreground-300 rounded-full text-sm hover:bg-background-600/60 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2 border border-background-600/40">
                      <i className="ri-heart-line"></i>
                      收藏
                    </button>
                    <button className="px-5 py-2.5 bg-background-700/60 text-foreground-300 rounded-full text-sm hover:bg-background-600/60 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2 border border-background-600/40">
                      <i className="ri-share-line"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-10 animate-fade-in-up animation-fill-both animation-delay-200">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-4">
                  剧情简介
                </h2>
                <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-3xl">
                  {movie.description}
                </p>
              </div>

              {relatedMovies.length > 0 && (
                <div className="mt-12 animate-fade-in-up animation-fill-both animation-delay-300">
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-5">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-product-shop="">
                    {relatedMovies.map((m) => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}